import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import UsageInfo from '../../components/UsageInfo';

// Menüde "Yakında" olarak duran, henüz açılamayan bölümler. Burada gösteriliyor ki
// otelci eksik bir modül aradığında planlanmış mı yoksa yok mu olduğunu bilsin.
const YAKINDA = [
  { ad: 'Online Satış / Kanal Yönetimi', ikon: '🌐', aciklama: 'Booking, Etstur gibi kanallarla oda-fiyat eşleştirme ve otomatik rezervasyon aktarımı' },
  { ad: 'Web Sitesi',                    ikon: '🖥️', aciklama: 'Otel tanıtım sitesi ve siteden doğrudan rezervasyon alma' },
  { ad: 'Acente & Firmalar',             ikon: '🤝', aciklama: 'Acente sözleşmeleri, kontenjan ve komisyon takibi' },
  { ad: 'SMS',                           ikon: '💬', aciklama: 'Rezervasyon ve tahsilat bilgilendirme mesajları' },
];

export default function Moduller() {
  const { modulleriYenile } = useAuth();
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState('');
  const [mesaj, setMesaj] = useState('');

  const getir = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try { setListe(await apiRequest('modul') || []); }
    catch (e) { setHata(e?.message || 'Modüller alınamadı.'); }
    finally { setYukleniyor(false); }
  }, []);

  useEffect(() => { getir(); }, [getir]);

  const degistir = (anahtar) =>
    setListe(p => p.map(m => m.anahtar === anahtar ? { ...m, aktifMi: !m.aktifMi } : m));

  const kaydet = async () => {
    setKaydediliyor(true); setHata(''); setMesaj('');
    try {
      await apiRequest('modul', 'PUT', liste.map(m => ({ anahtar: m.anahtar, aktifMi: m.aktifMi })));
      await modulleriYenile();          // menü anında güncellensin
      setMesaj('Modül ayarları kaydedildi. Menü güncellendi.');
      setTimeout(() => setMesaj(''), 4000);
    } catch (e) { setHata(e?.message || 'Kaydedilemedi.'); }
    finally { setKaydediliyor(false); }
  };

  if (yukleniyor) return <LoadingSpinner fullPage text="Modüller yükleniyor…" />;

  const acikSayi = liste.filter(m => m.aktifMi).length;

  // Sunucudan gelen sırayı koruyarak gruplara ayır
  const gruplar = liste.reduce((acc, m) => {
    const ad = m.grup || 'Modüller';
    const mevcut = acc.find(([g]) => g === ad);
    if (mevcut) mevcut[1].push(m); else acc.push([ad, [m]]);
    return acc;
  }, []);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl">
      <PageHeader
        icon="🧩"
        iconColor="from-sky-600 to-indigo-700"
        iconShadow="shadow-sky-200"
        title="Modüller"
        subtitle="Otelinizde kullanmadığınız özellikleri kapatın, menü sadeleşsin"
      >
        <button onClick={kaydet} disabled={kaydediliyor}
          className="px-5 py-2.5 rounded-2xl bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-xs font-black uppercase tracking-widest transition">
          {kaydediliyor ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </PageHeader>

      {hata && <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 text-sm font-semibold">{hata}</div>}
      {mesaj && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-4 text-sm font-semibold">{mesaj}</div>}

      <p className="text-sm text-slate-500">
        <strong className="text-slate-700">{acikSayi}</strong> / {liste.length} modül açık.
        Kapattığınız modülün menü girdisi gizlenir ve sayfasına erişilemez; verileri silinmez.
      </p>

      {gruplar.map(([grup, satirlar]) => (
        <div key={grup} className="space-y-3">
          <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-400">{grup}</h2>
          {satirlar.map(m => (
            <button key={m.anahtar} onClick={() => degistir(m.anahtar)}
              className={`w-full text-left bg-white rounded-[1.5rem] border p-5 flex items-center gap-4 transition ${
                m.aktifMi ? 'border-sky-200 shadow-sm' : 'border-slate-100 opacity-70'}`}>
              <span className="text-3xl font-emoji">{m.ikon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-black text-slate-800">{m.ad}</p>
                <p className="text-xs text-slate-500 mt-0.5">{m.aciklama}</p>
              </div>
              <span className={`relative w-14 h-8 rounded-full transition flex-shrink-0 ${m.aktifMi ? 'bg-sky-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${m.aktifMi ? 'left-7' : 'left-1'}`} />
              </span>
            </button>
          ))}
        </div>
      ))}

      <div className="space-y-3">
        <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-400">Geliştirme Aşamasında</h2>
        {YAKINDA.map(y => (
          <div key={y.ad}
            className="w-full bg-slate-50 rounded-[1.5rem] border border-dashed border-slate-200 p-5 flex items-center gap-4">
            <span className="text-3xl font-emoji grayscale opacity-60">{y.ikon}</span>
            <div className="flex-1 min-w-0">
              <p className="font-black text-slate-500">{y.ad}</p>
              <p className="text-xs text-slate-400 mt-0.5">{y.aciklama}</p>
            </div>
            <span className="text-[9px] font-black uppercase tracking-widest text-indigo-400 bg-indigo-50 px-2.5 py-1 rounded-lg flex-shrink-0">
              Yakında
            </span>
          </div>
        ))}
      </div>

      <UsageInfo
        color="sky"
        description="Her tesis her özelliği kullanmaz. Havuzu olmayan otelde havuz sayfası, hamamı olmayanda hamam randevusu menüyü kalabalıklaştırır. Kullanmadığınız hizmet ve modülleri buradan kapatabilirsiniz."
        scenarios={[
          { title: 'Hizmet açınca ne olur?', content: 'Hamam, sauna gibi bir hizmeti açtığınızda Otel Kurulumu → Ek Hizmetler sayfasında o hizmet hazır olarak oluşur. Kapasite, çalışma saatleri ve paket fiyatlarını oradan girersiniz.' },
          { title: 'Kapatınca ne olur?', content: 'Modülün menü girdisi gizlenir ve sayfasına adres yazarak da girilemez. Kayıtlı veriler, geçmiş randevu ve tahsilatlar silinmez; tekrar açtığınızda her şey yerinde durur.' },
          { title: 'Personel etkilenir mi?', content: 'Ayar otel geneli çalışır, tüm kullanıcılar için geçerlidir. Kişi bazlı erişim için Roller sayfasındaki yetkileri kullanın.' },
          { title: 'KBS neden listede yok?', content: 'Kimlik bildirimi 1774 sayılı Kimlik Bildirme Kanunu gereği her konaklama tesisi için zorunludur; kapatılabilir bir modül olarak sunulmaz.' },
        ]}
      />
    </div>
  );
}
